// StatusCell.tsx
import { useEffect, useRef, useState } from 'react';
import { ApartmentStatus } from '../CommonTypes';
import useSite from '../hooks/useSite';
import { useNavigate } from 'react-router-dom';
import { userData } from '../states/planner';
import { useAtom } from 'jotai';

const StatusCell = ({
  status,
  onChange,
  alwaysEdit,
}: {
  status: string;
  onChange?: (value: ApartmentStatus) => void;
  alwaysEdit?: boolean;
}) => {
  const [site] = useSite();
  const [user] = useAtom(userData);

  const [editMode, setEditMode] = useState(alwaysEdit || false);
  const [clicks, setClicks] = useState(0);
  const resetTimer = useRef<number | null>(null);
  const navigate = useNavigate();

  const isLoggedIn = user?.id !== 'anonymous';

  // Reset click counter if user pauses too long between clicks
  useEffect(() => {
    if (clicks === 0 || alwaysEdit) return;
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => setClicks(0), 1200); // 1.2s window between clicks
    return () => {
      if (resetTimer.current) window.clearTimeout(resetTimer.current);
    };
  }, [clicks, alwaysEdit]);

  const validateStatus = (s: string): ApartmentStatus => {
    switch (s) {
      case 'available':
      case 'sold':
      case 'reserved':
      case 'extra':
        return s;
      default:
        throw new Error('Invalid status');
    }
  };

  const enterEditMode = () => {
    if (!onChange) return;

    setEditMode(true);
    setClicks(0);
  };

  const handlePillClick = () => {
    setClicks(clicks + 1);
    if (isLoggedIn && clicks + 1 >= 2) enterEditMode();
    if (!isLoggedIn && clicks + 1 >= 5) navigate(`/${site.company}/admin`);
  };

  if (editMode && onChange) {
    return (
      <select
        name="status"
        id="status"
        className="h-8 w-[65px] appearance-none rounded-md border border-gray-300 bg-right bg-no-repeat pl-1 pr-0 text-xs md:w-36"
        onChange={e => {
          const v = validateStatus(e.target.value);
          onChange(v);
          setEditMode(alwaysEdit || false);
        }}
        defaultValue={validateStatus(status)}
        onBlur={() => setEditMode(alwaysEdit || false)}
      >
        {site.statuslabels.available && <option value="available">{site.statuslabels.available}</option>}
        {site.statuslabels.sold && <option value="sold">{site.statuslabels.sold}</option>}
        {site.statuslabels.reserved && <option value="reserved">{site.statuslabels.reserved}</option>}
        {site.statuslabels.extra && <option value="extra">{site.statuslabels.extra}</option>}
      </select>
    );
  }

  switch (status) {
    case 'available':
      return (
        <button
          type="button"
          onClick={handlePillClick}
          title={isLoggedIn ? 'Dubbelklicka för att ändra status' : ''}
          className={`-ml-1 flex ${isLoggedIn ? 'cursor-pointer' : 'cursor-default'} items-center bg-transparent p-0`}
        >
          <svg width="16" height="16" aria-hidden="true">
            <circle cx="8" cy="9" r="3" fill="#18974d" className="svg-circle-available" />
          </svg>
          <span className="whitespace-nowrap">{site?.statuslabels.available}</span>
        </button>
      );
    case 'sold':
      return (
        <button
          type="button"
          onClick={handlePillClick}
          title={isLoggedIn ? 'Dubbelklicka för att ändra status' : ''}
          className={`-ml-1 flex ${isLoggedIn ? 'cursor-pointer' : 'cursor-default'} items-center bg-transparent p-0`}
        >
          <svg width="16" height="16" aria-hidden="true">
            <circle cx="8" cy="9" r="3" fill="#ec3326" className="svg-circle-sold" />
          </svg>
          <span className="whitespace-nowrap">{site.statuslabels.sold}</span>
        </button>
      );
    case 'reserved':
      return (
        <button
          type="button"
          onClick={handlePillClick}
          title={isLoggedIn ? 'Dubbelklicka för att ändra status' : ''}
          className={`-ml-1 flex ${isLoggedIn ? 'cursor-pointer' : 'cursor-default'} items-center bg-transparent p-0`}
        >
          <svg width="16" height="16" aria-hidden="true">
            <circle cx="8" cy="9" r="3" fill="orange" className="svg-circle-reserved" />
          </svg>
          <span className="whitespace-nowrap">{site?.statuslabels.reserved}</span>
        </button>
      );
    case 'extra':
      return (
        <button
          type="button"
          onClick={handlePillClick}
          title={isLoggedIn ? 'Dubbelklicka för att ändra status' : ''}
          className={`-ml-1 flex ${isLoggedIn ? 'cursor-pointer' : 'cursor-default'} items-center bg-transparent p-0`}
        >
          <svg width="16" height="16" aria-hidden="true">
            <circle cx="8" cy="9" r="3" fill="cornflowerblue" className="svg-circle-extra" />
          </svg>
          <span className="whitespace-nowrap">{site?.statuslabels.extra}</span>
        </button>
      );
    default:
      return (
        <button
          type="button"
          onClick={handlePillClick}
          title={isLoggedIn ? 'Dubbelklicka för att ändra status' : ''}
          className={`-ml-1 flex ${isLoggedIn ? 'cursor-pointer' : 'cursor-default'} items-center bg-transparent p-0`}
        >
          <span>{status}</span>
        </button>
      );
  }
};

export default StatusCell;
